/**
 * Utility functions for safe property access
 */

export function getUserName(user?: {
  firstName?: string;
  lastName?: string;
  name?: string;
}): string {
  if (!user) return "Unknown";

  // If name is provided directly, use it
  if (user.name) return user.name;

  // Otherwise construct from firstName and lastName
  const parts: string[] = [];
  if (user.firstName) parts.push(user.firstName);
  if (user.lastName) parts.push(user.lastName);

  return parts.length > 0 ? parts.join(" ") : "Unknown";
}

export function getUserEmail(user?: { email?: string }): string {
  return user?.email || "-";
}

export function getUserPhone(user?: { phone?: string }): string {
  return user?.phone || "-";
}

export function getCustomerName(customer?: {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  if (!customer) return "Unknown";

  if (customer.name) return customer.name;

  const parts: string[] = [];
  if (customer.firstName) parts.push(customer.firstName);
  if (customer.lastName) parts.push(customer.lastName);

  return parts.length > 0 ? parts.join(" ") : "Unknown";
}

export function getCustomerEmail(customer?: { email?: string | null }): string {
  return customer?.email || "-";
}

export function getCustomerPhone(customer?: { phone?: string | null }): string {
  return customer?.phone || "-";
}

export function getLocationCoordinates(
  location?: unknown
): { lat: number; lng: number } | null {
  if (!location) return null;

  // Handle {lat, lng} object format
  if (
    typeof location === "object" &&
    location !== null &&
    "lat" in location &&
    "lng" in location
  ) {
    const loc = location as { lat: number; lng: number };
    return { lat: loc.lat, lng: loc.lng };
  }

  // Handle "lat,lng" string format (e.g., from waypoint.locationKey)
  if (typeof location === "string" && location.includes(",")) {
    const parts = location.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }
  }

  return null;
}

export function getAddressString(address?: {
  line1?: string;
  line2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}): string {
  if (!address) return "-";

  const parts: string[] = [];
  if (address.line1) parts.push(address.line1);
  if (address.line2) parts.push(address.line2);
  if (address.city) parts.push(address.city);
  if (address.province) parts.push(address.province);
  if (address.postalCode) parts.push(address.postalCode);

  return parts.length > 0 ? parts.join(", ") : "-";
}

export function safeAccess<T>(value: T | undefined | null, defaultValue: T): T {
  return value ?? defaultValue;
}

export function getOperatorInitials(operator?: {
  carrierUser?: {
    name?: string | null;
  };
}): string {
  const name = validOperatorName(operator);
  if (!name) return "?";
  const parts = name.split(/\s+/u);
  return `${parts[0]?.[0] ?? ""}${parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : ""}`.toLocaleUpperCase();
}

export function getOperatorDisplayName(operator?: {
  carrierUser?: { name?: string | null };
}): string {
  return validOperatorName(operator) ?? "Name required";
}

function validOperatorName(operator?: {
  carrierUser?: { name?: string | null };
}): string | null {
  const name = operator?.carrierUser?.name?.trim() ?? "";
  if (!name || /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(name)) return null;
  return name;
}

// Order helper functions for new data model
export function getOrderSenderInfo(order?: {
  shipments?: {
    addresses?: {
      type: string;
      name?: string;
      email?: string;
      phone?: string;
      address?: { line1?: string; city?: string };
    }[];
  }[];
}) {
  const shipment = order?.shipments?.[0];
  const fromAddress = shipment?.addresses?.find((a) => a.type === "FROM");

  return {
    name: fromAddress?.name || "Unknown",
    email: fromAddress?.email || "-",
    phone: fromAddress?.phone || "-",
    address: fromAddress?.address,
  };
}

export function getOrderRecipientInfo(order?: {
  shipments?: {
    addresses?: {
      type: string;
      name?: string;
      email?: string;
      phone?: string;
      address?: { line1?: string; city?: string };
    }[];
  }[];
}) {
  const shipment = order?.shipments?.[0];
  const toAddress = shipment?.addresses?.find((a) => a.type === "TO");

  return {
    name: toAddress?.name || "Unknown",
    email: toAddress?.email || "-",
    phone: toAddress?.phone || "-",
    address: toAddress?.address,
  };
}

export function calculateOrderTotal(order?: {
  shipments?: { quotes?: { retailRate?: number }[] }[];
}): number {
  if (!order?.shipments) return 0;

  return order.shipments.reduce((total, shipment) => {
    const shipmentTotal =
      shipment.quotes?.reduce(
        (sum, quote) => sum + (quote.retailRate ?? 0),
        0
      ) ?? 0;
    return total + shipmentTotal;
  }, 0);
}

// Shipment helper functions for export and other uses
export function getShipmentSenderInfo(shipment?: {
  addresses?: {
    type: string;
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      province?: string;
      postalCode?: string;
    };
  }[];
}) {
  const fromAddress = shipment?.addresses?.find((a) => a.type === "FROM");

  return {
    name: fromAddress?.name || "Unknown",
    email: fromAddress?.email || "-",
    phone: fromAddress?.phone || "-",
    address: fromAddress?.address,
  };
}

export function getShipmentRecipientInfo(shipment?: {
  addresses?: {
    type: string;
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      province?: string;
      postalCode?: string;
    };
  }[];
}) {
  const toAddress = shipment?.addresses?.find((a) => a.type === "TO");

  return {
    name: toAddress?.name || "Unknown",
    email: toAddress?.email || "-",
    phone: toAddress?.phone || "-",
    address: toAddress?.address,
  };
}
